'use client'

import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import Markdown from 'react-markdown'
import { Zap, Shield, Gauge, Users, Code, Cloud } from 'lucide-react'
import { cn } from '../../../lib/cn'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../../lib/motion'

/**
 * FeaturesGrid - 3-column centered grid with icons
 *
 * **Layout:**
 * ```
 * ┌─────────────────────────────────────────────┐
 * │            Centered Section Title            │
 * ├─────────────┬─────────────┬─────────────────┤
 * │   [icon]    │   [icon]    │    [icon]       │
 * │   Title     │   Title     │    Title        │
 * │   Desc      │   Desc      │    Desc         │
 * ├─────────────┼─────────────┼─────────────────┤
 * │   [icon]    │   [icon]    │    [icon]       │
 * │   Title     │   Title     │    Title        │
 * │   Desc      │   Desc      │    Desc         │
 * └─────────────┴─────────────┴─────────────────┘
 * ```
 *
 * **CONSTRAINTS:**
 * - Best with 3, 6, or 9 features for balanced grid
 * - Icons displayed in circular accent background
 * - Text is always centered
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Supports any number of features, but multiples of 3 look best
 * - Each feature needs `heading`, `description`, and optional `icon`
 * - Title is optional but recommended
 * - Icons should be 24x24 (size-6) for consistency
 */
export interface FeaturesGridFeature {
  heading: string
  /** Feature description - supports Markdown (bold, code, links) */
  description: string
  icon?: ReactNode
}

export interface FeaturesGridProps {
  /** Section title - centered above grid */
  title?: string
  /** Feature items */
  features?: FeaturesGridFeature[]
  /** Number of columns on large screens (default: 3) */
  columns?: 2 | 3
  className?: string
}

const defaultFeatures: FeaturesGridFeature[] = [
  {
    heading: 'Lightning Fast',
    description: 'Optimized for speed with zero runtime overhead. Your app stays fast.',
    icon: <Zap className="size-4" />,
  },
  {
    heading: 'Secure by Default',
    description: 'Enterprise-grade security with automatic updates and vulnerability scanning.',
    icon: <Shield className="size-4" />,
  },
  {
    heading: 'High Performance',
    description: 'Built for scale with intelligent caching and edge deployment.',
    icon: <Gauge className="size-4" />,
  },
  {
    heading: 'Team Collaboration',
    description: 'Work together seamlessly with real-time editing and version control.',
    icon: <Users className="size-4" />,
  },
  {
    heading: 'Developer Experience',
    description: 'Intuitive APIs and comprehensive documentation for rapid development.',
    icon: <Code className="size-4" />,
  },
  {
    heading: 'Cloud Native',
    description: 'Deploy anywhere with first-class support for all major cloud providers.',
    icon: <Cloud className="size-4" />,
  },
]

const FeaturesGrid = ({
  title,
  features = defaultFeatures,
  columns = 3,
  className,
}: FeaturesGridProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {title && (
          <motion.div className="mx-auto mb-16 max-w-3xl text-center" variants={headerAnimation}>
            <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">
              {title}
            </h2>
          </motion.div>
        )}
        <motion.div
          className={cn('grid gap-10 md:grid-cols-2', columns === 3 && 'lg:grid-cols-3')}
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {features.map((feature) => (
            <motion.div
              key={feature.heading}
              className={cn('flex flex-col items-center text-center', columns === 2 && 'max-w-md mx-auto')}
              variants={itemAnimation}
            >
              {feature.icon && (
                <div className="bg-accent/50 mb-5 flex size-10 items-center justify-center rounded-[calc(var(--radius)*2)] shadow-sm">
                  {feature.icon}
                </div>
              )}
              <p className="mb-2 text-xl font-semibold">{feature.heading}</p>
              <div className="prose prose-sm max-w-none text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                <Markdown>{feature.description}</Markdown>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { FeaturesGrid }
